
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    inquiryType: 'general'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSubmit = new URLSearchParams();
      formDataToSubmit.append('name', formData.name);
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('subject', formData.subject);
      formDataToSubmit.append('message', formData.message);
      formDataToSubmit.append('inquiryType', formData.inquiryType);

      const response = await fetch('https://readdy.ai/api/form/d3i72fukn7r41cr5kkjg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSubmit.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          inquiryType: 'general'
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-slate-800 mb-6">Get in Touch</h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            I'd love to hear from you! Whether you have questions about my books, 
            speaking opportunities, or just want to connect, feel free to reach out.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-6">Send a Message</h2>
              
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800">Thank you for your message! I'll get back to you soon.</p>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800">There was an error sending your message. Please try again.</p>
                </div>
              )}

              <form onSubmit={handleSubmit} data-readdy-form>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 text-sm"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 text-sm"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="inquiryType" className="block text-sm font-medium text-slate-700 mb-2">
                    Inquiry Type
                  </label>
                  <select
                    id="inquiryType"
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 text-sm pr-8"
                  >
                    <option value="general">General Inquiry</option>
                    <option value="media">Media & Press</option>
                    <option value="speaking">Speaking Engagement</option>
                    <option value="collaboration">Book Collaboration</option>
                    <option value="bulk">Bulk Book Orders</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 text-sm"
                    placeholder="Brief subject of your message"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 text-sm resize-none"
                    placeholder="Your message (max 500 characters)"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-blue-800 hover:bg-blue-900" 
                  disabled={isSubmitting}
                  size="lg"
                >
                  {isSubmitting ? 'Sending Message...' : 'Send Message'}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Let's Connect</h2>
                <p className="text-lg text-slate-600 mb-8">
                  I'm always excited to connect with readers, fellow authors, and anyone interested 
                  in personal growth and purposeful living. Here are the best ways to reach me:
                </p>
              </div>

              {/* Contact Methods */}
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-slate-100 rounded-lg">
                    <i className="ri-mail-line text-xl text-slate-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1">Email</h3>
                    <p className="text-slate-600">hello@elbertcalexander.com</p>
                    <p className="text-sm text-slate-500">I typically respond within 24-48 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-slate-100 rounded-lg">
                    <i className="ri-calendar-line text-xl text-slate-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1">Speaking Engagements</h3>
                    <p className="text-slate-600">Available for workshops, conferences, and events</p>
                    <p className="text-sm text-slate-500">Book speaking opportunities in advance</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-slate-100 rounded-lg">
                    <i className="ri-newspaper-line text-xl text-slate-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1">Media Inquiries</h3>
                    <p className="text-slate-600">Press kit and interview requests welcome</p>
                    <p className="text-sm text-slate-500">High-resolution photos and bio available</p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-4">Follow My Journey</h3>
                <div className="flex space-x-4">
                  <a href="#" className="w-12 h-12 flex items-center justify-center bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors cursor-pointer">
                    <i className="ri-instagram-line text-xl"></i>
                  </a>
                  <a href="#" className="w-12 h-12 flex items-center justify-center bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors cursor-pointer">
                    <i className="ri-twitter-x-line text-xl"></i>
                  </a>
                  <a href="#" className="w-12 h-12 flex items-center justify-center bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors cursor-pointer">
                    <i className="ri-facebook-line text-xl"></i>
                  </a>
                  <a href="#" className="w-12 h-12 flex items-center justify-center bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors cursor-pointer">
                    <i className="ri-youtube-line text-xl"></i>
                  </a>
                  <a href="#" className="w-12 h-12 flex items-center justify-center bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors cursor-pointer">
                    <i className="ri-linkedin-line text-xl"></i>
                  </a>
                </div>
              </div>

              {/* Response Times */}
              <div className="bg-slate-50 rounded-lg p-6">
                <h3 className="font-semibold text-slate-800 mb-3">Response Times</h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex justify-between">
                    <span>General inquiries:</span>
                    <span>24-48 hours</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Speaking requests:</span>
                    <span>3-5 business days</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Media inquiries:</span>
                    <span>Same day</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Collaboration proposals:</span>
                    <span>1 week</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-12 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {[
              {
                question: "Do you offer bulk discounts for organizations?",
                answer: "Yes! I offer special pricing for bulk orders of 25+ books. Contact me with your specific needs and I'll provide a custom quote."
              },
              {
                question: "Are you available for speaking engagements?",
                answer: "Absolutely! I speak at conferences, workshops, and corporate events on topics related to personal growth, entrepreneurship, and faith-based leadership."
              },
              {
                question: "Can I interview you for my podcast or publication?",
                answer: "I love connecting with media! Please include details about your audience, format, and timeline in your inquiry."
              },
              {
                question: "Do you offer coaching or consulting services?",
                answer: "While I don't offer one-on-one coaching, I do provide group workshops and corporate training sessions. Contact me for more information."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="font-semibold text-slate-800 mb-2">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
